import _ from 'lodash';
import fs from 'fs-extra';
import path from 'path';
import stringify from 'json-stringify-safe';
import Promise from 'bluebird';
import { log, now, config } from '~/utils';

/*
 * Services that need event logging:
 *
 * CardsService
 *  [ ] card.created/card.updated/card.deleted
 *  [ ] card.lost/card.found
 *  [x] card.swiped (SessionsService)
 * ErrorsService
 *  [ ] error.reported
 * GuestsService
 *  [ ] guest.created
 *  [ ] guest.updated/guest.deleted
 *  [ ] guest.arrived/guest.departed (SessionsService)
 * MembersService
 *  [ ] member.created/member.updated/member.deleted
 *  [ ] member.arrived/member.departed (SessionsService)
 * MembershipsService
 *  [ ] membership.created/membership.updated/membership.deleted
 *  [ ] membership.arrived/membership.departed (SessionsService)
 * SessionsService
 *  [x] session.opened
 *  [ ] session.closed
 *  [ ] session.archived
 *  [ ] session.abandoned
 *  [x] session.member-in/session.member-out
 *  [x] session.friend-in/session.friend-out
 *  [x] session.guest-in/session.guest-out
 *  [x] guest.arrived/guest.departed (SessionsService)
 *  [x] member.arrived/member.departed (SessionsService)
 * StationsService
 *  [ ] station.online
 *  [ ] station.offline
 * SwipesService
 *  [x] card.swiped
 *
 * Now instead of later
 *
 * GuestsService
 *  created
 * SessionsService
 *  created/updated
 * SwipesService
 *  swiped
 */

class EventLogEvent {
  /**
   * Timestamp of the event.
   */
  ts;
  event;
  sessionId;
  readerId;
  memberId;
  membershipId;
  guestId;
  friendId;
}

class EventLogStore {

  constructor( opts={} ) {
    _.assign( this, opts );
    if ( ! this.directory ) {
      throw new Error( `EventLogStore requires directory` );
    }
    fs.ensureDirSync( this.directory );
    this.streams = {};
  }

  write( data ) {
    // We re-order the fields so that the timestamp is at the front
    // (making it easy to sort/merge log entries from different
    // systems without having to parse every record) and to normalize
    // the order of the remaining fields.
    const initialfields = [
      'ts', 'event', 'readerId', 'sessionId',
    ];
    const datafields = _.keys( data ).sort();
    const fields = _.uniq( [ ...initialfields, ...datafields ] );
    const logdata = {};
    _.each( fields, key => {
      if ( _.isNil( data[ key ] ) ) return;
      logdata[ key ] = data[ key ];
    } );
    const stream = this.getStream( data.ts.slice( 0, 10 ) );
    stream.write( stringify( logdata ) + '\n' );
    return logdata;
  }

  getStream( date ) {
    return this.streams[ date ] || this.createStream( date );
  }

  createStream( date ) {
    const file = path.resolve( this.directory, `${date}.ndjson` );
    this.streams[ date ] = fs.createWriteStream( file, {
      flags     : 'a',
      encoding  : 'utf8',
    } );
    _.each( this.streams, ( stream, d ) => {
      if ( d === date ) return;
      stream.end();
      delete this.streams[ d ];
    } );
    return this.streams[ date ];
  }

}

export default function createEventlogHook( opts={} ) {
  const store = new EventLogStore( {
    directory : config.dir( 'eventlogs' ),
  } );
  return function eventlogHook( context ) {
    const { type, service, method, params } = context;
    const label = [ service.name, type, method ].join( '.' );
    const { eventlog } = params;
    if ( ! eventlog ) {
      throw new Error( `${label} did not provide eventlog data` );
    }
    if ( _.isArray( eventlog ) ) {
      return Promise.map( eventlog, el => recordEvent( context, el ) );
    } else {
      return recordEvent( context, eventlog );
    }
  };
  function recordEvent( context, eventlog ) {
    // context properties
    //    app         The feathers app (readonly)
    //    service     The service (readonly)
    //    path        Service path (readonly)
    //    method      Service method (readonly)
    //    type        Hook type (before/after/error) (readonly)
    //    params      Service method parameters
    //    id          Resource id for get/remove/update/patch
    //    data        Resource data for create/update/patch
    //    error       Error instance (if type==='error')
    //    result      The result of the method call
    //    dispatch    "Safe" version of the data to send to client
    //    statusCode  HTTP status code
    const { type, service, method, params, error } = context;
    const label = [ service.name, type, method ].join( '.' );
    const p = _.propertyOf( params );
    _.defaults( eventlog, {
      // These are the minimum properties that an eventlog entry
      // should include
      success       : !error,
      memberId      : p( 'member.id' ),
      membershipId  : p( 'member.membershipId' ),
      stationId     : p( 'station.id' ),
    } );
    _.assign( eventlog, {
      ts : now(),
    } );
    const missing = _.filter( [
      // These properties must be defined in the eventlog entries
      'event',
    ], key => _.isNil( eventlog[ key ] ) );
    if ( missing.length ) {
      throw new Error( `Eventlog from ${label} missing ${missing}` );
    }
    const undef = _.filter( [
      // These properties don't have to be included, but if they are
      // included they have to have an actual value
    ], key => {
      if ( ! _.has( eventlog, key ) ) return false;
      const val = eventlog[ key ];
      if ( _.isNil( val ) ) return true;
      if ( _.isString( val ) && ! val.length ) return true;
      return false;
    } );
    if ( undef.length ) {
      throw new Error( `Eventlog from ${label} didn't define ${undef}` );
    }
    log.debug( `[EVENTLOG] ${label}`, store.write( eventlog ) );
    return;
  }
}
